import { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "../../../design/utils/cn";
import { useLobby } from "../../match/lobby/hooks/useLobby";
import LobbyRoomCreatedInvite from "../../match/lobby/components/LobbyRoomCreatedInvite";
import { writeStoredSession } from "../../../utils/storedSession";
import { useActiveSport } from "../../../sports/ActiveSportContext";
import re from "../../room-experience/styles/roomExperience.module.css";
import s from "../styles/lobby.module.css";
import { scrollSpanIntoView } from "../scrollSpanIntoView";

/**
 * Inline Create / Join form for Arena hub — slides open under CTAs (no /play navigation).
 * Sport inherits Global Active Sport Mode — no per-create picker.
 */
export default function ArenaCreateJoinPanel({
  mode = "create",
  open = false,
  onModeChange,
  /** Home court — when set, opening create scrolls from this element to Create Arena. */
  focusFromRef = null,
}) {
  const navigate = useNavigate();
  const { presentation } = useActiveSport();
  const headingRef = useRef(null);
  const submitRef = useRef(null);
  const stayRef = useRef(null);

  const enterRoomExperience = (payload) => {
    if (!payload?.room) return;
    writeStoredSession({
      room: payload.room,
      role: payload.role,
      roomDisplayName: payload.roomDisplayName,
      view: "match",
      ...(payload.sportId ? { sportId: payload.sportId } : {}),
      ...(payload.registerAsCreator ? { registerAsCreator: true } : {}),
      ...(payload.registerAsCreator && payload.creatorPlaysInSession === false
        ? { creatorPlaysInSession: false }
        : {}),
    });
    navigate(`/room/${encodeURIComponent(payload.room)}/session`, { replace: true });
  };

  const {
    tab,
    switchTab,
    rName,
    setRName,
    refC,
    setRefC,
    viewC,
    setViewC,
    joinC,
    setJoinC,
    passC,
    setPassC,
    err,
    loading,
    submit,
    passInputRef,
    creatorPlaysInSession,
    setCreatorPlaysInSession,
    createInvite,
    enterCreatedRoom,
    dismissCreateInvite,
  } = useLobby({
    onJoin: enterRoomExperience,
    initialTab: mode === "join" ? "join" : "create",
  });

  useEffect(() => {
    if (!open) return;
    const next = mode === "join" ? "join" : "create";
    if (tab !== next) switchTab(next);
  }, [open, mode, tab, switchTab]);

  const heading = useMemo(
    () => (tab === "join" ? "Join an arena" : "Create an arena"),
    [tab]
  );

  useEffect(() => {
    if (!open || mode !== "create" || createInvite || !focusFromRef) return undefined;
    const timer = window.setTimeout(() => {
      scrollSpanIntoView(focusFromRef.current, submitRef.current);
    }, 360);
    return () => window.clearTimeout(timer);
  }, [open, mode, createInvite, focusFromRef]);

  useEffect(() => {
    if (!open || !createInvite || !focusFromRef) return undefined;
    const timer = window.setTimeout(() => {
      scrollSpanIntoView(headingRef.current, stayRef.current);
    }, 80);
    return () => window.clearTimeout(timer);
  }, [open, createInvite, focusFromRef]);

  return (
    <div
      className={cn(s.slidePanel, open && s.slidePanelOpen)}
      aria-hidden={!open}
    >
      <div className={s.slidePanelInner}>
        <div className={s.slidePanelBody} data-testid="arena-create-join-panel">
          <p ref={headingRef} className={s.slidePanelEyebrow}>{heading}</p>

          <div className={re.fields}>
            {tab === "create" && createInvite ? (
              <LobbyRoomCreatedInvite
                roomCode={createInvite.joinPayload.room}
                roomLabel={createInvite.joinPayload.roomDisplayName}
                spectatorCode={createInvite.spectatorCode}
                refereeCode={createInvite.refCode}
                inviteAbsUrl={createInvite.inviteAbsUrl || ""}
                inviteReady={createInvite.inviteReady !== false}
                inviteError={createInvite.inviteError || ""}
                onContinue={() => enterCreatedRoom()}
                onDismiss={dismissCreateInvite}
                dismissRef={stayRef}
                continueLabel="Continue to arena"
                dismissLabel="Stay here"
              />
            ) : tab === "create" ? (
              <>
                <p className={re.hint} data-testid="arena-create-active-sport">
                  {presentation.emoji ? `${presentation.emoji} ` : ""}
                  {presentation.displayName} arena
                </p>
                <div className={re.field}>
                  <label className={re.label} htmlFor="arena-hub-room-name">
                    Arena label
                  </label>
                  <input
                    id="arena-hub-room-name"
                    className={re.input}
                    value={rName}
                    onChange={(e) => setRName(e.target.value)}
                    placeholder="e.g. Morning group"
                    disabled={!open}
                  />
                </div>
                <p className={re.hint}>
                  After create you get a 7-character arena code to share, plus QR and join link.
                </p>
                <div className={re.field}>
                  <label className={re.label} htmlFor="arena-hub-ref-code">
                    Referee password
                  </label>
                  <input
                    id="arena-hub-ref-code"
                    className={re.input}
                    type="password"
                    value={refC}
                    onChange={(e) => setRefC(e.target.value)}
                    placeholder="Set a password"
                    disabled={!open}
                  />
                </div>
                <div className={re.field}>
                  <label className={re.label} htmlFor="arena-hub-view-code">
                    Player access code <span className={re.opt}>(optional)</span>
                  </label>
                  <input
                    id="arena-hub-view-code"
                    className={re.input}
                    value={viewC}
                    onChange={(e) => setViewC(e.target.value)}
                    placeholder="Blank = open arena"
                    disabled={!open}
                  />
                </div>
                <label className={re.check}>
                  <input
                    type="checkbox"
                    checked={creatorPlaysInSession}
                    onChange={(e) => setCreatorPlaysInSession(e.target.checked)}
                    disabled={!open}
                  />
                  <span>I am playing in this session</span>
                </label>
              </>
            ) : (
              <>
                <div className={re.field}>
                  <label className={re.label} htmlFor="arena-hub-join-code">
                    Arena code
                  </label>
                  <input
                    id="arena-hub-join-code"
                    className={re.input}
                    value={joinC}
                    onChange={(e) => setJoinC(e.target.value)}
                    placeholder="From invite link, QR, or Share"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    disabled={!open}
                  />
                </div>
                <div className={re.field}>
                  <label className={re.label} htmlFor="arena-hub-access-code">
                    Access code
                  </label>
                  <input
                    ref={passInputRef}
                    id="arena-hub-access-code"
                    className={re.input}
                    type="password"
                    value={passC}
                    onChange={(e) => setPassC(e.target.value)}
                    placeholder="Referee password or player access"
                    disabled={!open}
                  />
                </div>
              </>
            )}

            {err ? <p className={re.error}>{err}</p> : null}

            {!(tab === "create" && createInvite) ? (
              <button
                ref={submitRef}
                type="button"
                className={re.primaryCta}
                disabled={!open || loading}
                onClick={() => void submit()}
              >
                {loading ? "Working…" : tab === "create" ? "Create Arena" : "Join Arena"}
              </button>
            ) : null}
          </div>

          {onModeChange && !(tab === "create" && createInvite) ? (
            <button
              type="button"
              className={s.slidePanelSwitch}
              disabled={!open}
              onClick={() => onModeChange(tab === "create" ? "join" : "create")}
            >
              {tab === "create" ? "Have a code? Join instead" : "Need a new arena? Create instead"}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
